import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import "../styles/RefundManagement.css";

// ─── GraphQL ────────────────────────────────────────────────────────────────

const GET_ORDERS_WITH_RETURNS = gql`
  query GetOrdersWithReturns($status: String) {
    ordersWithReturns(status: $status) {
      orderId
      buyer {
        name
        email
        phone
      }
      payment {
        mode
        paymentStatus
      }
      items {
        productId
        name
        price
        quantity
        image
      }
      returns {
        productId
        sellerId
        reason
        status
        refundStatus
        refundAmount
        requestedAt
        approvedAt
        receivedAt
        refundedAt
        refundId
      }
      createdAt
    }
  }
`;

const REVIEW_RETURN = gql`
  mutation ReviewReturnRequest($input: ReviewReturnInput!) {
    reviewReturnRequest(input: $input) {
      orderId
      returns {
        productId
        status
      }
    }
  }
`;

const MARK_RECEIVED = gql`
  mutation MarkItemReceived($input: MarkReceivedInput!) {
    markItemReceived(input: $input) {
      orderId
      returns {
        productId
        status
      }
    }
  }
`;

const QUALITY_CHECK = gql`
  mutation QualityCheckReturn($input: QualityCheckInput!) {
    qualityCheckReturn(input: $input) {
      orderId
      returns {
        productId
        status
        refundStatus
      }
    }
  }
`;

const INITIATE_REFUND = gql`
  mutation InitiateRefund($input: InitiateRefundInput!) {
    initiateRefund(input: $input) {
      orderId
      returns {
        productId
        status
        refundStatus
        refundId
      }
    }
  }
`;

// ─── Status Config ───────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  REQUESTED:        { label: "Requested",        color: "#f59e0b", bg: "#fef3c7", icon: "🔔" },
  APPROVED:         { label: "Approved",          color: "#3b82f6", bg: "#dbeafe", icon: "✅" },
  REJECTED:         { label: "Rejected",          color: "#ef4444", bg: "#fee2e2", icon: "❌" },
  RECEIVED:         { label: "Item Received",     color: "#8b5cf6", bg: "#ede9fe", icon: "📦" },
  QC_PASSED:        { label: "QC Passed",         color: "#06b6d4", bg: "#cffafe", icon: "🔍" },
  REFUND_INITIATED: { label: "Refund Initiated",  color: "#10b981", bg: "#d1fae5", icon: "💸" },
  REFUNDED:         { label: "Refunded",          color: "#059669", bg: "#a7f3d0", icon: "✔️" },
  PICKED_UP:        { label: "Picked Up",         color: "#6366f1", bg: "#e0e7ff", icon: "🚚" },
  PENDING_MANUAL:   { label: "Manual Pending",    color: "#d97706", bg: "#fde68a", icon: "🏦" },
};

const REFUND_STATUS_CONFIG = {
  NOT_APPLICABLE: { label: "N/A",         color: "#6b7280" },
  PENDING:        { label: "Pending",     color: "#f59e0b" },
  PROCESSING:     { label: "Processing",  color: "#3b82f6" },
  PROCESSED:      { label: "Processed",   color: "#10b981" },
  PAID:           { label: "Paid",        color: "#059669" },
  FAILED:         { label: "Failed",      color: "#ef4444" },
};

const STATUS_STEPS = ["REQUESTED","APPROVED","RECEIVED","QC_PASSED","REFUND_INITIATED","REFUNDED"];

// ─── Helper Components ───────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#6b7280", bg: "#f3f4f6", icon: "•" };
  return (
    <span className="rm-status-badge" style={{ color: cfg.color, background: cfg.bg }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

function RefundStatusBadge({ status }) {
  const cfg = REFUND_STATUS_CONFIG[status] || { label: status, color: "#6b7280" };
  return <span className="rm-refund-status" style={{ color: cfg.color }}>● {cfg.label}</span>;
}

function StepProgress({ status }) {
  const idx = STATUS_STEPS.indexOf(status);
  return (
    <div className="rm-step-bar">
      {STATUS_STEPS.map((s, i) => (
        <div key={s} className={`rm-step ${i < idx ? "done" : i === idx ? "active" : ""}`}>
          <div className="rm-step-dot" />
          <span className="rm-step-label">{STATUS_CONFIG[s]?.label || s}</span>
          {i < STATUS_STEPS.length - 1 && <div className="rm-step-line" />}
        </div>
      ))}
    </div>
  );
}

// ─── Return Row Card ─────────────────────────────────────────────────────────

function ReturnCard({ order, returnItem, onRefetch }) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const [reviewReturn]  = useMutation(REVIEW_RETURN);
  const [markReceived]  = useMutation(MARK_RECEIVED);
  const [qualityCheck]  = useMutation(QUALITY_CHECK);
  const [initiateRefund]= useMutation(INITIATE_REFUND);

  const item = order.items?.find(i => i.productId === returnItem.productId);

  const act = async (label, fn) => {
    setLoading(true);
    setMsg(null);
    try {
      await fn();
      setMsg({ type: "success", text: `${label} successful` });
      onRefetch();
    } catch (e) {
      setMsg({ type: "error", text: e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove  = () => act("Approval",  () => reviewReturn({ variables: { input: { orderId: order.orderId, productId: returnItem.productId, approve: true  } } }));
  const handleReject   = () => act("Rejection", () => reviewReturn({ variables: { input: { orderId: order.orderId, productId: returnItem.productId, approve: false } } }));
  const handleReceived = () => act("Mark Received", () => markReceived({ variables: { input: { orderId: order.orderId, productId: returnItem.productId } } }));
  const handleQCPass   = () => act("QC Pass",  () => qualityCheck({ variables: { input: { orderId: order.orderId, productId: returnItem.productId, approve: true  } } }));
  const handleQCFail   = () => act("QC Fail",  () => qualityCheck({ variables: { input: { orderId: order.orderId, productId: returnItem.productId, approve: false } } }));
  const handleRefund   = () => act("Refund",   () => initiateRefund({ variables: { input: { orderId: order.orderId, productId: returnItem.productId } } }));

  const { status, refundStatus } = returnItem;
  const isOnline = order.payment?.mode === "ONLINE";

  return (
    <div className="rm-return-card">
      {/* Header */}
      <div className="rm-card-header">
        <div className="rm-card-ids">
          <span className="rm-order-id">🧾 {order.orderId}</span>
          <span className="rm-product-id">PID: {returnItem.productId}</span>
        </div>
        <div className="rm-card-badges">
          <StatusBadge status={status} />
          <span className={`rm-payment-mode ${isOnline ? "online" : "cod"}`}>
            {isOnline ? "💳 Online" : "💵 COD"}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <StepProgress status={status} />

      {/* Body */}
      <div className="rm-card-body">
        {/* Product */}
        <div className="rm-section">
          <h4 className="rm-section-title">Product</h4>
          <div className="rm-product-row">
            {item?.image && <img src={item.image} alt={item?.name} className="rm-product-img" />}
            <div>
              <div className="rm-product-name">{item?.name || "—"}</div>
              <div className="rm-product-meta">
                ₹{item?.price?.toLocaleString("en-IN")} × {item?.quantity}
              </div>
            </div>
          </div>
        </div>

        {/* Return Info */}
        <div className="rm-section">
          <h4 className="rm-section-title">Return Details</h4>
          <table className="rm-details-table">
            <tbody>
              <tr><td>Reason</td><td>{returnItem.reason || "—"}</td></tr>
              <tr><td>Refund Amount</td><td>₹{returnItem.refundAmount?.toLocaleString("en-IN") || "—"}</td></tr>
              <tr><td>Requested</td><td>{returnItem.requestedAt ? new Date(Number(returnItem.requestedAt)).toLocaleDateString("en-IN") : "—"}</td></tr>
              {returnItem.approvedAt && <tr><td>Approved</td><td>{new Date(Number(returnItem.approvedAt)).toLocaleDateString("en-IN")}</td></tr>}
              {returnItem.refundId && <tr><td>Refund ID</td><td><code>{returnItem.refundId}</code></td></tr>}
              {refundStatus && refundStatus !== "NOT_APPLICABLE" && (
                <tr><td>Refund Status</td><td><RefundStatusBadge status={refundStatus} /></td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Buyer */}
        <div className="rm-section">
          <h4 className="rm-section-title">Buyer</h4>
          <div className="rm-buyer-info">
            <span>👤 {order.buyer?.name || "—"}</span>
            <span>📧 {order.buyer?.email || "—"}</span>
            <span>📞 {order.buyer?.phone || "—"}</span>
          </div>
        </div>
      </div>

      {/* Action Footer */}
      <div className="rm-card-footer">
        {msg && <span className={`rm-msg ${msg.type}`}>{msg.text}</span>}

        {status === "REQUESTED" && (
          <>
            <button className="rm-btn approve" onClick={handleApprove} disabled={loading}>✅ Approve</button>
            <button className="rm-btn reject"  onClick={handleReject}  disabled={loading}>❌ Reject</button>
          </>
        )}

        {status === "APPROVED" && (
          <button className="rm-btn received" onClick={handleReceived} disabled={loading}>📦 Mark Item Received</button>
        )}

        {status === "RECEIVED" && (
          <>
            <button className="rm-btn qcpass" onClick={handleQCPass} disabled={loading}>✔️ QC Pass</button>
            <button className="rm-btn qcfail" onClick={handleQCFail} disabled={loading}>✘ QC Fail</button>
          </>
        )}

        {status === "QC_PASSED" && (
          isOnline ? (
            <button className="rm-btn refund" onClick={handleRefund} disabled={loading}>💸 Process Refund</button>
          ) : (
            <div className="rm-manual-refund">
              🏦 <strong>Manual Refund Required</strong> — COD order, transfer ₹{returnItem.refundAmount?.toLocaleString("en-IN")} manually.
            </div>
          )
        )}

        {status === "REJECTED" && (
          <span className="rm-msg-inline rejected">Return Rejected — No further action needed.</span>
        )}

        {(status === "REFUND_INITIATED" || status === "REFUNDED") && (
          <span className="rm-msg-inline success">
            {status === "REFUNDED" ? "✔️ Refund Complete" : "⏳ Refund Processing via Razorpay webhook..."}
          </span>
        )}

        {loading && <span className="rm-loader">⏳ Processing...</span>}
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

const STATUS_FILTERS = [
  { label: "All",             value: "" },
  { label: "🔔 Requested",    value: "REQUESTED" },
  { label: "✅ Approved",     value: "APPROVED" },
  { label: "📦 Received",     value: "RECEIVED" },
  { label: "🔍 QC Passed",    value: "QC_PASSED" },
  { label: "💸 Refund Init.", value: "REFUND_INITIATED" },
  { label: "✔️ Refunded",     value: "REFUNDED" },
  { label: "❌ Rejected",     value: "REJECTED" },
];

export default function RefundManagement() {
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const { data, loading, error, refetch } = useQuery(GET_ORDERS_WITH_RETURNS, {
    variables: { status: statusFilter || undefined },
    fetchPolicy: "cache-and-network",
  });

  // Flatten orders → individual return rows
  const rows = (data?.ordersWithReturns || []).flatMap(order =>
    (order.returns || []).map(ret => ({ order, returnItem: ret }))
  );

  // Client-side text search
  const filtered = rows.filter(({ order, returnItem }) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      order.orderId?.toLowerCase().includes(q) ||
      order.buyer?.name?.toLowerCase().includes(q) ||
      order.buyer?.email?.toLowerCase().includes(q) ||
      returnItem.productId?.toLowerCase().includes(q) ||
      returnItem.reason?.toLowerCase().includes(q)
    );
  });

  // Summary counts
  const counts = {};
  rows.forEach(({ returnItem }) => {
    counts[returnItem.status] = (counts[returnItem.status] || 0) + 1;
  });

  return (
    <div className="rm-page">
      {/* Page Header */}
      <div className="rm-page-header">
        <div>
          <h1 className="rm-page-title">🔄 Refund Management</h1>
          <p className="rm-page-sub">Review and process return &amp; refund requests end-to-end.</p>
        </div>
        <button className="rm-btn refresh" onClick={() => refetch()}>↻ Refresh</button>
      </div>

      {/* Summary Strip */}
      <div className="rm-summary-strip">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) =>
          counts[key] ? (
            <div key={key} className="rm-summary-chip" style={{ borderColor: cfg.color }}>
              <span style={{ color: cfg.color }}>{cfg.icon} {cfg.label}</span>
              <strong style={{ color: cfg.color }}>{counts[key]}</strong>
            </div>
          ) : null
        )}
        {!rows.length && !loading && <span className="rm-no-counts">No return requests found.</span>}
      </div>

      {/* Filters */}
      <div className="rm-filters">
        <input
          className="rm-search"
          type="text"
          placeholder="🔍 Search by order ID, buyer, product..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="rm-filter-tabs">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              className={`rm-tab ${statusFilter === f.value ? "active" : ""}`}
              onClick={() => { setStatusFilter(f.value); refetch({ status: f.value || undefined }); }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* States */}
      {loading && (
        <div className="rm-state-box">
          <div className="rm-spinner" />
          <p>Loading return requests...</p>
        </div>
      )}

      {error && (
        <div className="rm-state-box error">
          <p>❌ {error.message}</p>
          <button className="rm-btn" onClick={() => refetch()}>Retry</button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="rm-state-box">
          <p className="rm-empty-icon">📭</p>
          <p>No return requests {search ? `matching "${search}"` : "found"}.</p>
        </div>
      )}

      {/* Cards Grid */}
      {!loading && !error && filtered.length > 0 && (
        <div className="rm-grid">
          {filtered.map(({ order, returnItem }) => (
            <ReturnCard
              key={`${order.orderId}-${returnItem.productId}`}
              order={order}
              returnItem={returnItem}
              onRefetch={refetch}
            />
          ))}
        </div>
      )}

      <p className="rm-count-label">
        Showing {filtered.length} return{filtered.length !== 1 ? "s" : ""}
        {statusFilter ? ` with status "${statusFilter}"` : ""}
        {search ? ` matching "${search}"` : ""}
      </p>
    </div>
  );
}